/**
 * Drag & Drop Track Reordering Hook
 * Provides reusable drag and drop functionality for track reordering
 */
import { useState, useCallback, useRef } from 'react';

export const useDragDropTracks = ({ tracks, onReorder }) => {
  const [draggedTrackId, setDraggedTrackId] = useState(null);
  const [dragOverTrackId, setDragOverTrackId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'before' | 'after'
  const dragStartIndex = useRef(null);

  const handleDragStart = useCallback((e, trackId, index) => {
    setDraggedTrackId(trackId);
    dragStartIndex.current = index;
    
    // Set drag image (optional - uses default if not set)
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', trackId.toString());
      
      // Create custom drag image
      const dragImage = document.createElement('div');
      dragImage.className = 'bg-zinc-800 px-4 py-2 rounded-lg border border-cyan-500 text-white text-sm shadow-xl';
      dragImage.textContent = tracks.find(t => t.id === trackId)?.name || `Track ${index + 1}`;
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-1000px';
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, 50, 20);
      
      // Clean up after a short delay
      setTimeout(() => document.body.removeChild(dragImage), 0);
    }
  }, [tracks]);

  const handleDragOver = useCallback((e, trackId, index, customPosition = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (trackId === draggedTrackId) {
      setDragOverTrackId(null);
      setDropPosition(null);
      return;
    }
    
    setDragOverTrackId(trackId);
    
    // Use custom position if provided (for horizontal layouts), otherwise calculate from mouse Y
    if (customPosition) {
      setDropPosition(customPosition);
    } else {
      // Determine if dropping before or after based on mouse position (vertical layout)
      const rect = e.currentTarget.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      const position = e.clientY < midpoint ? 'before' : 'after';
      setDropPosition(position);
    }
  }, [draggedTrackId]);

  const handleDragLeave = useCallback((e) => {
    // Only clear if leaving the track container entirely
    const relatedTarget = e.relatedTarget;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDragOverTrackId(null);
      setDropPosition(null);
    }
  }, []);

  const handleDrop = useCallback((e, targetTrackId, targetIndex) => {
    e.preventDefault();
    
    if (draggedTrackId === null || draggedTrackId === targetTrackId) {
      setDraggedTrackId(null);
      setDragOverTrackId(null);
      setDropPosition(null);
      return;
    }
    
    const sourceIndex = tracks.findIndex(t => t.id === draggedTrackId);
    if (sourceIndex === -1) return;
    
    // Calculate new index based on drop position
    let newIndex = targetIndex;
    if (dropPosition === 'after') {
      newIndex = targetIndex + 1;
    }
    
    // Adjust for the item being removed
    if (sourceIndex < newIndex) {
      newIndex -= 1;
    }
    
    // Reorder tracks
    const newTracks = [...tracks];
    const [movedTrack] = newTracks.splice(sourceIndex, 1);
    newTracks.splice(newIndex, 0, movedTrack);
    
    onReorder?.(newTracks);
    
    // Reset state
    setDraggedTrackId(null);
    setDragOverTrackId(null);
    setDropPosition(null);
    dragStartIndex.current = null;
  }, [draggedTrackId, dropPosition, tracks, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDraggedTrackId(null);
    setDragOverTrackId(null);
    setDropPosition(null);
    dragStartIndex.current = null;
  }, []);

  return {
    draggedTrackId,
    dragOverTrackId,
    dropPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    isDragging: draggedTrackId !== null,
  };
};

/**
 * Drag handle styles for track items
 */
export const getDragStyles = ({ isDragged, isDragOver, dropPosition }) => {
  const baseStyles = 'transition-all duration-150';
  
  if (isDragged) {
    return `${baseStyles} opacity-50 scale-95`;
  }
  
  if (isDragOver) {
    const borderStyle = dropPosition === 'before' 
      ? 'border-t-2 border-t-cyan-500' 
      : 'border-b-2 border-b-cyan-500';
    return `${baseStyles} ${borderStyle} bg-cyan-500/5`;
  }
  
  return baseStyles;
};

export default useDragDropTracks;
